import React from "react";
import { formatMessage } from "lib/globalize-util";
import {
    mergeStyleSets,
    nuOrderTheme as theme,
    Spinner,
    SpinnerSize,
    MessageBarType,
    ISpinnerStyles,
} from "@nuorder/components";
import {
    useInfiniteFetchItemPermissions,
    useUpdateItemPermission,
    useDeleteItemPermission,
    useCreateItemPermission,
} from "../../../../hooks/space-item";
import { DeletePermissionDialog } from "../dialogs";
import { generateLowerBoundaryRoleMap } from "containers/folders/utils/permissions";
import { fetchMoreDelayed, flattenInfiniteData } from "containers/folders/hooks/utils";
import { DATATEST } from "constants/integration";
import siteConfig from "site-config";
import { MessageNotification } from "containers/folders/containers/space-manager/components/utils/messageNotification";
import { ILowerBoundaryRoleMap } from "containers/folders/types/base";
import { ManageAccessPermissionList } from "containers/folders/containers/space-manager/components/manage-access/manage-access-permission-list/manage-access-permission-list";
import { ManageAccessForm } from "containers/folders/containers/space-manager/components/manage-access/manage-access-form";
import { ITEM_SHARED_LIST_MAX_HEIGHT } from "containers/folders/utils/constants";
import { useFetchGetMerchant } from "hooks/useFetchGetMerchant";

interface IShareItemPanelProps {
    selectedItemId: string;
    merchantId: string;
    sharedItemMerchantId?: string;
    externalBrandName?: string;
    externalContactMerchantId?: string;
    isExternalBrandPortalLoading?: boolean;
    externalBrandPortalErrorMessage?: string;
    onDialogDismiss: () => void;
    isCloseoutBrandAssortment?: boolean;
    closeoutTargetMerchantId?: string;
}

const MODAL_BACKGROUND_HEIGHT = 510;
export const generateCreatePermissionError = (createItemPermissionReturnData) => {
    const errorObject = (createItemPermissionReturnData || []).filter((data) => data?.code);
    if (errorObject.length === 0) {
        return undefined;
    }
    const errorMessage = errorObject.map((err) => err?.message).join(",");
    return errorMessage;
};

export const permissionFilterHelper = (
    externalContactMerchantId: string,
    granteeMerchantId: string,
    merchantId: string,
    sharedItemMerchantId?: string,
    closeoutTargetMerchantId?: string
) => {
    const isExternal =
        granteeMerchantId &&
        granteeMerchantId !== merchantId &&
        granteeMerchantId !== siteConfig.NUORDER_SUPPORT_MERCHANT_ID &&
        granteeMerchantId !== sharedItemMerchantId;
    const isInternal =
        granteeMerchantId &&
        (granteeMerchantId === merchantId ||
            granteeMerchantId === siteConfig.NUORDER_SUPPORT_MERCHANT_ID ||
            granteeMerchantId == sharedItemMerchantId);
    const isCloseout = granteeMerchantId === closeoutTargetMerchantId;

    return closeoutTargetMerchantId ? isCloseout : externalContactMerchantId ? isExternal : isInternal;
};

export const ManageAccessComponent: React.FC<IShareItemPanelProps> = ({
    selectedItemId,
    merchantId,
    sharedItemMerchantId,
    externalBrandName,
    externalContactMerchantId,
    isExternalBrandPortalLoading,
    externalBrandPortalErrorMessage,
    onDialogDismiss,
    isCloseoutBrandAssortment = false,
    closeoutTargetMerchantId,
}) => {
    const [isDeletePermissionDialogOpen, setIsDeletePermissionDialogOpen] = React.useState<boolean>(false);
    const [selectedPermission, setSelectedPermission] = React.useState<nuorder.folder.v1.Permission>(undefined);
    const {
        data,
        isFetching,
        isFetchingMore,
        fetchMore,
        canFetchMore,
        error: getPermissionError,
    } = useInfiniteFetchItemPermissions(merchantId, selectedItemId, {}, { enabled: !!merchantId && !!selectedItemId });
    const [
        updateItemPermission,
        { isLoading: isUpdatePermissionLoading, error: updatePermissionError, reset: resetUpdatePermission },
    ] = useUpdateItemPermission(merchantId, selectedItemId);
    const [
        deleteItemPermission,
        {
            isLoading: isDeletePermissionLoading,
            isSuccess: isDeletePermissionSuccess,
            error: deletePermissionError,
            reset: resetDeletePermission,
        },
    ] = useDeleteItemPermission(
        merchantId,
        selectedItemId,
        selectedPermission?.id,
        () => setIsDeletePermissionDialogOpen(false),
        () => setIsDeletePermissionDialogOpen(true)
    );
    const { data: targetMerchant, isFetching: isFetchingTargetMerchant } =
        useFetchGetMerchant(closeoutTargetMerchantId);
    const [
        createItemPermission,
        {
            data: createItemPermissionReturnData,
            isLoading: isCreateItemPermissionisLoading,
            reset: resetCreateItemPermission,
        },
    ] = useCreateItemPermission(merchantId, selectedItemId);

    React.useEffect(() => {
        return () => {
            resetDeletePermission();
        };
    }, [resetDeletePermission]);

    React.useMemo(
        () => fetchMoreDelayed(canFetchMore, isFetchingMore, isFetching, fetchMore),
        [canFetchMore, isFetchingMore, isFetching, fetchMore]
    );
    const classes = React.useMemo(
        () =>
            mergeStyleSets({
                itemSharedList: {
                    margin: "10px 0 10px 0",
                    height: `calc(100vh - ${MODAL_BACKGROUND_HEIGHT}px)`,
                    maxHeight: `${ITEM_SHARED_LIST_MAX_HEIGHT}px`,
                    overflowY: "auto",
                    borderBottom: `1px solid ${theme.palette.neutralLight}`,
                    padding: "0",
                    "& [data-automation-key='options']": {
                        display: "flex",
                        justifyContent: "flex-end",
                    },
                    "& [data-item-key='options']": {
                        marginLeft: "3vw",
                    },
                },
                externalBrandContainer: {
                    fontSize: "14px",
                    color: theme.palette.greyDark,
                },
                externalBrandName: {
                    fontWeight: "bold",
                    color: theme.palette.black,
                    marginLeft: "8px",
                },
            }),
        []
    );
    const spinnerStyle = React.useMemo<Partial<ISpinnerStyles>>(
        () => ({
            root: {
                backgroundColor: theme.palette.white,
                position: "relative",
                height: "100%",
                width: "100%",
                zIndex: 999,
            },
        }),
        []
    );
    const itemPermissions = React.useMemo(() => {
        return flattenInfiniteData(data, (data) => data.permissions);
    }, [data]);
    const displayItemPermissions = React.useMemo(
        () =>
            itemPermissions.filter((permission) =>
                permissionFilterHelper(
                    externalContactMerchantId,
                    permission.granted_to.merchant_id,
                    merchantId,
                    sharedItemMerchantId,
                    closeoutTargetMerchantId
                )
            ),
        [itemPermissions, externalContactMerchantId, merchantId, sharedItemMerchantId, closeoutTargetMerchantId]
    );

    const LowerBoundaryRoleMap = React.useMemo(
        () => generateLowerBoundaryRoleMap(displayItemPermissions),
        [displayItemPermissions]
    );

    const handleConfirmDeletePermission = React.useCallback(
        () => {
            setIsDeletePermissionDialogOpen(false);
            deleteItemPermission({ merchantId, itemId: selectedItemId, permissionId: selectedPermission?.id });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            setIsDeletePermissionDialogOpen,
            deleteItemPermission,
            resetDeletePermission,
            selectedPermission,
            selectedItemId,
            merchantId,
        ]
    );

    const handleCloseDeletePermission = React.useCallback(() => {
        setIsDeletePermissionDialogOpen(false);
        resetDeletePermission();
        resetUpdatePermission();
        resetCreateItemPermission();
    }, [setIsDeletePermissionDialogOpen, resetDeletePermission, resetDeletePermission, resetUpdatePermission]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleOpenDeletePermissionDialog = React.useCallback(() => {
        setIsDeletePermissionDialogOpen(true);
        resetDeletePermission();
        resetUpdatePermission();
        resetCreateItemPermission();
    }, [setIsDeletePermissionDialogOpen, resetDeletePermission, resetUpdatePermission, resetCreateItemPermission]);

    const handleUpdateItemPermission = React.useCallback(
        (params) => {
            resetUpdatePermission();
            const {
                currentPermission,
                LowerBoundaryRoleMap,
                merchantId,
                itemId,
                memberId,
                type,
                role,
                memberMerchantId,
                originalRole,
            } = params;
            const updateParams = { merchantId, itemId, memberId, type, role, memberMerchantId, originalRole };
            if (updateParams.role !== currentPermission.role) {
                if (LowerBoundaryRoleMap[params.memberId]) {
                    if (currentPermission.role === LowerBoundaryRoleMap[params.memberId]) {
                        createItemPermission({
                            merchantId: merchantId,
                            itemId: selectedItemId,
                            role: role,
                            memberIds: [params.memberId],
                            memberMerchantIds: [memberMerchantId],
                            kind: currentPermission.granted_to.kind,
                        });
                    } else {
                        if (updateParams.role === LowerBoundaryRoleMap[params.memberId]) {
                            deleteItemPermission({
                                merchantId,
                                itemId: selectedItemId,
                                permissionId: currentPermission?.id,
                            });
                        } else {
                            updateItemPermission(updateParams);
                        }
                    }
                } else {
                    updateItemPermission(updateParams);
                }
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [resetUpdatePermission]
    );
    const createPermissionError = generateCreatePermissionError(createItemPermissionReturnData);
    const showSpinner =
        isCreateItemPermissionisLoading ||
        (isExternalBrandPortalLoading && !externalBrandPortalErrorMessage) ||
        ((canFetchMore || isFetchingMore || isFetching) && !getPermissionError);
    const isChangePermissionLoading =
        isUpdatePermissionLoading || isDeletePermissionLoading || isCreateItemPermissionisLoading;
    const errorMessage =
        (getPermissionError as any)?.message ||
        externalBrandPortalErrorMessage ||
        createPermissionError ||
        updatePermissionError?.message;
    const manageAccessPickerLabel = isCloseoutBrandAssortment
        ? formatMessage("Invite buyers")
        : externalBrandName
          ? formatMessage("Invite brand recipients")
          : formatMessage("Invite internal recipients");

    return (
        <section data-test={DATATEST.ASSORTMENTS.SHARE_RECIPIENTS_CONTAINER}>
            {errorMessage && (
                <MessageNotification
                    message={errorMessage}
                    dataTest={DATATEST.FOLDERS.GRANT_ACCESS.ERROR_MESSAGE_BAR}
                    messageBarType={MessageBarType.error}
                    onDismiss={() => {
                        resetUpdatePermission();
                        resetCreateItemPermission();
                    }}
                />
            )}
            <div className={classes.itemSharedList} data-is-scrollable="true">
                {showSpinner ? (
                    <Spinner
                        data-test={DATATEST.LOADING_SPINNER}
                        label={formatMessage("Loading...")}
                        size={SpinnerSize.large}
                        styles={spinnerStyle}
                    />
                ) : (
                    <ManageAccessPermissionList
                        canFetchMore={canFetchMore}
                        data-test={DATATEST.FOLDERS.GRANT_ACCESS.PERSONA_LIST}
                        LowerBoundaryRoleMap={LowerBoundaryRoleMap as ILowerBoundaryRoleMap}
                        personaData={displayItemPermissions}
                        selectedItemId={selectedItemId}
                        merchantId={merchantId}
                        onUpdateItempermission={handleUpdateItemPermission}
                        onOpenDeletePermissionDialog={handleOpenDeletePermissionDialog}
                        onSetSelectedPermission={setSelectedPermission}
                        shouldExcludeOrganizerPermission={!!externalContactMerchantId || isCloseoutBrandAssortment}
                        isChangePermissionLoading={isChangePermissionLoading}
                        selectedPermission={selectedPermission}
                    />
                )}
            </div>
            {(externalBrandName || isCloseoutBrandAssortment) && (
                <div className={classes.externalBrandContainer}>
                    {isCloseoutBrandAssortment ? formatMessage("Merchant:") : formatMessage("Brand:")}
                    <span className={classes.externalBrandName}>
                        {isCloseoutBrandAssortment ? targetMerchant?.name : externalBrandName}
                    </span>
                </div>
            )}
            <ManageAccessForm
                sharedItemMerchantId={sharedItemMerchantId}
                disableForm={!!getPermissionError || !!externalBrandPortalErrorMessage}
                data-test={DATATEST.FOLDERS.GRANT_ACCESS.MANAGE_ACCESS_FORM}
                merchantId={merchantId}
                externalContactMerchantId={externalContactMerchantId}
                itemId={selectedItemId}
                itemPermissions={itemPermissions}
                onCreateItemPermission={createItemPermission}
                LowerBoundaryRoleMap={LowerBoundaryRoleMap as ILowerBoundaryRoleMap}
                isExternalShare={!!externalContactMerchantId || isCloseoutBrandAssortment}
                pickerLabel={`${manageAccessPickerLabel}:`}
                onDialogDismiss={onDialogDismiss}
                closeoutTargetMerchantId={closeoutTargetMerchantId}
            />
            <DeletePermissionDialog
                data-test={DATATEST.FOLDERS.GRANT_ACCESS.DELETE_PERMISSION_DIALOG}
                isBlocking={true}
                open={isDeletePermissionDialogOpen}
                onConfirm={handleConfirmDeletePermission}
                onClose={handleCloseDeletePermission}
                isLoading={isDeletePermissionLoading}
                disableConfirm={isDeletePermissionLoading || isDeletePermissionSuccess}
                selectedPermission={selectedPermission}
                errorMessage={(deletePermissionError as any)?.message}
            />
        </section>
    );
};
